import { lazyWebResolvers } from "outlook-resolvers-web";
import { lazyOfflineResolvers } from "outlook-resolvers-offline";
import { lazyPstResolvers } from "outlook-resolvers-pst";
import { lazyUnifiedInboxResolvers } from "outlook-resolvers-uib";
import { lazyMessageFileResolvers } from "outlook-resolvers-file";
import { lazyPstUibResolvers } from "./lazyPstUibResolvers";
import type { ResolverImports } from "owa-apollo-links";
import type { Resolvers } from "owa-graph-schema";
import { isSafeModeSession } from "owa-config/lib/isSafeModeSession";

let offlineEnabledResolvers: ResolverImports;
let nonOfflineEnabledResolvers: ResolverImports;
let pstResolvers: Resolvers;
let unifiedInboxResolvers: Resolvers;
let messageFileResolver: Resolvers;
let mergedPstUibResolvers: {
    pstResolvers: Resolvers;
    unifiedInboxResolvers: Resolvers;
} | undefined;

export async function getResolvers(offlineEnabled = false, pstEnabled = false, unifiedInboxEnabled = false, messageFileEnabled = false, mergeResolverChunksEnabled = false): Promise<ResolverImports> {
    let rv: ResolverImports;
    if (!nonOfflineEnabledResolvers) {
        nonOfflineEnabledResolvers = { web: await lazyWebResolvers.import() };
    }
    
    rv = nonOfflineEnabledResolvers;
    
    if (isSafeModeSession()) {
        return rv;
    }
    
    if (offlineEnabled) {
        if (!offlineEnabledResolvers) {
            offlineEnabledResolvers = {
                ...rv,
                offline: await lazyOfflineResolvers.import(),
            };
        }
        
        rv = offlineEnabledResolvers;
    }
    
    // Unified Inbox is only enabled if offline is enabled
    const unifiedInboxReallyEnabled = unifiedInboxEnabled && offlineEnabled;
    
    if (mergeResolverChunksEnabled && (pstEnabled || unifiedInboxReallyEnabled)) {
        // Load pst/unifiedInbox as a single merged chunk to avoid two blocking importScripts()
        // calls on the worker's single JS thread during boot. The resolver maps stay distinct --
        // each is still assigned to its own rv key below, exactly as the per-chunk path does.
        if (!mergedPstUibResolvers) {
            mergedPstUibResolvers = await lazyPstUibResolvers.import();
        }
        
        if (pstEnabled) {
            rv = { ...rv, pst: mergedPstUibResolvers.pstResolvers };
        }
        
        if (unifiedInboxReallyEnabled) {
            rv = { ...rv, unifiedInbox: mergedPstUibResolvers.unifiedInboxResolvers };
        }
    }
    else {
        if (pstEnabled) {
            if (!pstResolvers) {
                pstResolvers = await lazyPstResolvers.import();
            }
            
            rv = {
                ...rv,
                pst: pstResolvers,
            };
        }
        
        if (unifiedInboxReallyEnabled) {
            if (!unifiedInboxResolvers) {
                unifiedInboxResolvers = await lazyUnifiedInboxResolvers.import();
            }
            
            rv = {
                ...rv,
                unifiedInbox: unifiedInboxResolvers,
            };
        }
    }
    
    if (messageFileEnabled) {
        if (!messageFileResolver) {
            messageFileResolver = await lazyMessageFileResolvers.import();
        }
        
        rv = {
            ...rv,
            messageFile: messageFileResolver,
        };
    }
    
    return rv;
}
